import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { LocationData } from '@/services/locations/locations.types';

import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import useUpdateLocationStatus from '../hooks/useUpdateLocationStatus';
import { LocationsTableActionBar } from './ActionBar';
import { getColumns } from './Column';

export function LocationsTable({
  data,
  pagination,
  loading,
}: {
  data: LocationData[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { onOpen } = useDialogFormStore();
  const { mutateAsyncLocationStatus } = useUpdateLocationStatus();
  const { openConfirmation } = useConfirmationStore();
  const { t } = useDynamicLocale();

  const columns = React.useMemo(
    () => getColumns(onOpen, mutateAsyncLocationStatus, openConfirmation, t),
    [onOpen, mutateAsyncLocationStatus, openConfirmation, t],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'locationId', desc: true }],
    },
    meta: {
      title: <CrumbTitle id="configurationManagement.page.table.location" />,
      totalPage: pagination.totalItems,
      loading: loading,
    },
    getRowId: (originalRow: LocationData) => String(originalRow.locationId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
        <LocationsTableActionBar />
      </DataTable>
    </>
  );
}
